import PropTypes from 'prop-types'
import React from 'react'
import { Button, Input } from 'reactstrap'

const propTypes = {
  currentPage    : PropTypes.number,
  totalPages     : PropTypes.number,
  totalCount     : PropTypes.number,
  perPage        : PropTypes.number,
  perPageOptions : PropTypes.arrayOf(PropTypes.number),
  isLoading      : PropTypes.bool,
  onPageChange   : PropTypes.func,
  onPerPageChange: PropTypes.func
}

const defaultProps = {
  currentPage    : 1,
  totalPages     : 1,
  totalCount     : 0,
  perPage        : 25,
  perPageOptions : [ 25, 50, 100 ],
  isLoading      : false,
  onPageChange   : () => {},
  onPerPageChange: () => {}
}

const buildDisplayedPages = (currentPage, totalPages) => {
  if (totalPages <= 7) {
    return Array.from({ length: totalPages }, (_value, index) => index + 1)
  }

  const pages = [ 1 ]
  const rangeStart = Math.max(2, currentPage - 1)
  const rangeEnd = Math.min(totalPages - 1, currentPage + 1)

  if (rangeStart > 2) pages.push('start-ellipsis')

  for (let page = rangeStart; page <= rangeEnd; page += 1) {
    pages.push(page)
  }

  if (rangeEnd < totalPages - 1) pages.push('end-ellipsis')

  pages.push(totalPages)
  return pages
}

const Phase3QueuePagination = ({
  currentPage,
  totalPages,
  totalCount,
  perPage,
  perPageOptions,
  isLoading,
  onPageChange,
  onPerPageChange
}) => {
  const safeCurrentPage = Math.max(1, currentPage || 1)
  const safeTotalPages = Math.max(1, totalPages || 1)
  const safeTotalCount = Math.max(0, totalCount || 0)
  const firstItem = safeTotalCount === 0 ? 0 : ((safeCurrentPage - 1) * perPage) + 1
  const lastItem = safeTotalCount === 0 ? 0 : Math.min(safeCurrentPage * perPage, safeTotalCount)
  const displayedPages = buildDisplayedPages(safeCurrentPage, safeTotalPages)

  const goToPage = (nextPage) => {
    if (isLoading || nextPage < 1 || nextPage > safeTotalPages || nextPage === safeCurrentPage) return
    onPageChange(nextPage)
  }

  return (
    <div className='d-flex flex-column flex-xl-row justify-content-between align-items-xl-center border-top px-3 py-3'>
      <div className='text-muted mb-3 mb-xl-0'>
        Menampilkan { firstItem }–{ lastItem } dari { safeTotalCount } produk
        <span className='ml-2'>· Halaman { safeCurrentPage } dari { safeTotalPages }</span>
      </div>

      <div className='d-flex flex-column flex-md-row align-items-md-center'>
        <div className='d-flex align-items-center mb-2 mb-md-0 mr-md-3'>
          <label htmlFor='phase3-queue-per-page' className='mb-0 mr-2 text-muted' style={{ whiteSpace: 'nowrap' }}>
            Baris per halaman
          </label>
          <Input
            id='phase3-queue-per-page'
            type='select'
            bsSize='sm'
            value={ perPage }
            disabled={ isLoading }
            onChange={ (event) => onPerPageChange(Number(event.target.value)) }
            style={{ width: 82 }}
          >
            { perPageOptions.map((option) => (
              <option key={ option } value={ option }>{ option }</option>
            )) }
          </Input>
        </div>

        <div className='d-flex align-items-center flex-wrap' style={{ gap: 6 }}>
          <Button
            type='button'
            size='sm'
            color='secondary'
            outline
            disabled={ isLoading || safeCurrentPage <= 1 }
            onClick={ () => goToPage(1) }
          >
            Pertama
          </Button>
          <Button
            type='button'
            size='sm'
            color='secondary'
            outline
            disabled={ isLoading || safeCurrentPage <= 1 }
            onClick={ () => goToPage(safeCurrentPage - 1) }
          >
            Sebelumnya
          </Button>

          { displayedPages.map((pageItem) => {
            if (typeof pageItem !== 'number') {
              return <span key={ pageItem } className='px-1 text-muted'>…</span>
            }

            const isActive = pageItem === safeCurrentPage
            return (
              <Button
                key={ pageItem }
                type='button'
                size='sm'
                color={ isActive ? 'primary' : 'secondary' }
                outline={ !isActive }
                disabled={ isLoading || isActive }
                aria-current={ isActive ? 'page' : undefined }
                onClick={ () => goToPage(pageItem) }
                style={{ minWidth: 34 }}
              >
                { pageItem }
              </Button>
            )
          }) }

          <Button
            type='button'
            size='sm'
            color='secondary'
            outline
            disabled={ isLoading || safeCurrentPage >= safeTotalPages }
            onClick={ () => goToPage(safeCurrentPage + 1) }
          >
            Berikutnya
          </Button>
          <Button
            type='button'
            size='sm'
            color='secondary'
            outline
            disabled={ isLoading || safeCurrentPage >= safeTotalPages }
            onClick={ () => goToPage(safeTotalPages) }
          >
            Terakhir
          </Button>
        </div>
      </div>
    </div>
  )
}

Phase3QueuePagination.propTypes = propTypes
Phase3QueuePagination.defaultProps = defaultProps

export default Phase3QueuePagination
